import {Device, ManualEntry} from "../core/datamodel";
import {ViewState} from "../states";
import {get, patch, post} from "../core/request";
import {
    Button,
    Card,
    Checkbox,
    DatePicker,
    InputNumber,
    message,
    Modal,
    Progress,
    Result,
    Skeleton,
    Space,
    Steps,
    theme,
    Tooltip
} from "antd";
import React, {createRef, forwardRef, useEffect, useImperativeHandle, useState} from "react";
import RichTextEditor, {DEFAULT_COLORS} from "./richTextEditor";
import {LoadingOutlined} from "@ant-design/icons";
import {AsyncImage} from "./asyncImage";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import {AsyncTable} from "./asyncTable";
import {PdfPreview} from "./pdfPreview";
import {ContentBar} from "./contentBar";
import {CustomButton} from "./buttons";

dayjs.extend(timezone)

const {useToken} = theme;

interface ReportEditorProps {
    device: Device | null,
}

const Center = (props: any) => {
    // @ts-ignore
    return <span style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    }} {...props}/>
}

const breakLine = (s: string) => s.split('\n').map((line: string) => <>{line}<br/></>)

export const YearV1ReportEditor = (props: ReportEditorProps) => {
    const [state, setState] = useState(ViewState.initial);
    const [loadingStep, setLoadingStep] = useState(0);
    const [date, setDate] = useState(dayjs().subtract(1, 'years').endOf('year'));
    const [reportData, setReportData] = useState({});
    const [reportBody, setReportBody] = useState([]);
    const [step, setStep] = useState(0);
    const [excelAvailable, setExcelAvailable] = useState(false);

    useEffect(() => {
        console.debug("reportData", reportData);
        if ('excel_available' in reportData) {
            setExcelAvailable(reportData['excel_available'] as boolean)
        }
        // @ts-ignore
        if ('parts' in reportData && reportData['parts'] !== null) {
            let body: any[] = []
            // @ts-ignore
            for (let part of reportData['parts']) {
                switch (part['type']) {
                    case 'layout-text':
                        body = [...body, {
                            type: 'layout-text',
                            ref: part['content'],
                            item: <p
                                style={{textAlign: part['align'] ? part['align'] : 'left'}}>{breakLine(part['content'])}</p>
                        }]
                        break;
                    case 'text':
                        body = [...body, {
                            type: 'text',
                            ref: part['content'],
                            item: <p
                                style={{textAlign: part['align'] ? part['align'] : 'left'}}>{breakLine(part['content'])}</p>
                        }]
                        break;
                    case 'editor':
                        let editor = createRef();
                        body = [...body, {
                            type: 'editor',
                            ref: editor,
                            item: <RichTextEditor
                                ref={editor}
                                colors={DEFAULT_COLORS}
                                content={part['content']}
                                placeholder={"Hier Freitext eingeben..."}
                            />
                        }]
                        break;
                    case 'address':
                        let address = createRef();
                        body = [...body, {
                            type: 'address',
                            ref: address,
                            item: <RichTextEditor
                                disableFormating
                                ref={address}
                                colors={DEFAULT_COLORS}
                                content={part['content']}
                                placeholder={"Hier Freitext eingeben..."}
                            />
                        }]
                        break;
                    case 'subject':
                        let subject = createRef();
                        body = [...body, {
                            type: 'subject',
                            ref: subject,
                            item: <RichTextEditor
                                disableFormating
                                ref={subject}
                                colors={DEFAULT_COLORS}
                                content={part['content']}
                                placeholder={"Hier Freitext eingeben..."}
                            />
                        }]
                        break;
                    case 'image':
                        body = [...body, {
                            type: 'image',
                            ref: part['uuid'],
                            item: <Center><AsyncImage style={{maxHeight: 250, width: 'auto'}} uuid={part['uuid']}/><br/></Center>
                        }]
                        break;
                    case 'table':
                        let table = createRef();
                        body = [...body, {
                            type: 'table',
                            ref: table,
                            item: <Center><AsyncTable ref={table} uuid={part['uuid']}/></Center>

                        }]
                        break;
                    default:
                        break;
                }
                console.debug("part", part);
            }
            // @ts-ignore
            setReportBody(body);
        } else {
            setReportBody([]);
        }
    }, [reportData])

    const onFetch = () => {
        if (date !== null) {
            setState(ViewState.loading)
            setStep(1);

            // @ts-ignore
            get("devices/" + props.device?.props.uuid + "/reports/year?year=" + date.format("YYYY"))
                .done((response) => {
                        setState(ViewState.success)
                        setReportData(response.data.report)
                    }
                )
                .fail(() => setState(ViewState.failed))
        } else {
            message.warning("Jahr benötigt!")
        }
    }

    const onSubmit = () => {
        setStep(2);
        let report: any[] = []
        for (let part of reportBody) {
            // @ts-ignore
            if (part.type === 'editor') {
                // @ts-ignore
                report = [...report, {type: 'editor', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'address') {
                // @ts-ignore
                report = [...report, {type: 'address', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'subject') {
                // @ts-ignore
                report = [...report, {type: 'subject', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'image') {
                // @ts-ignore
                report = [...report, {type: 'image', uuid: part.ref}]
            }
            // @ts-ignore
            if (part.type === 'text') {
                // @ts-ignore
                report = [...report, {type: 'text', content: part.ref}]
            }
            // @ts-ignore
            if (part.type === 'layout-text') {
                // @ts-ignore
                report = [...report, {type: 'layout-text', content: part.ref}]
            }
            // @ts-ignore
            if (part.type === 'table') {
                // @ts-ignore
                report = [...report, {type: 'table', table: part.ref.current.content}]
            }
        }
        post(
            "devices/" + props.device?.props.uuid + "/reports/year",
            {
                'report': report,
                'year': date.format("YYYY")
            }
        )
    }

    const preview = () => {
        let report: any[] = []
        for (let part of reportBody) {
            // @ts-ignore
            if (part.type === 'editor') {
                // @ts-ignore
                report = [...report, {type: 'editor', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'address') {
                // @ts-ignore
                report = [...report, {type: 'address', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'subject') {
                // @ts-ignore
                report = [...report, {type: 'subject', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'image') {
                // @ts-ignore
                report = [...report, {type: 'image', uuid: part.ref}]
            }
            // @ts-ignore
            if (part.type === 'text') {
                // @ts-ignore
                report = [...report, {type: 'text', content: part.ref}]
            }
            // @ts-ignore
            if (part.type === 'layout-text') {
                // @ts-ignore
                report = [...report, {type: 'layout-text', content: part.ref}]
            }
            // @ts-ignore
            if (part.type === 'table') {
                // @ts-ignore
                report = [...report, {type: 'table', table: part.ref.current.content}]
            }
        }
        post(
            "devices/" + props.device?.props.uuid + "/reports/year?preview=True",
            {
                'report': report,
                'year': date.format("YYYY")
            }
        ).done(
            (response) => {
                // show pdf preview in modal
                Modal.info({
                    maskClosable: true,
                    closable: true,
                    okText: 'Schließen',
                    content: <PdfPreview uuid={response.data.job_uuid}/>,
                    width: 'calc(21cm + 48px)',
                    footer: null,
                    icon: null,
                    bodyStyle: {
                        width: '21cm',
                        paddingTop: 34,
                        height: 'calc(80vh-34px)',
                    },
                    style: {
                        minHeight: '80vh',
                        maxWidth: 'calc(21cm + 48px)',
                    },
                    className: 'pdf-preview',
                });
            }
        )
    }


    const dateSelector = (
        <Space size={24} style={{width: "100%", justifyContent: "center"}}>
            Jahr: <DatePicker.YearPicker
            defaultValue={
                dayjs().subtract(1, 'years').endOf('year')
            }
            format={"YYYY"}
            placement={'bottomRight'}
            // @ts-ignore
            onChange={(value) => setDate(value)}
        />
            <Button onClick={onFetch} children={"Bericht abrufen"}/>
        </Space>
    )

    const loadingText = () => {
        setTimeout(() => setLoadingStep((loadingStep + 1) % 4), 3000);
        return {
            0: "Bericht wird vorbereitet...",
            1: "Jahresdaten werden abgerufen...",
            2: "Tabelle wird erzeugt...",
            3: "Texte werden generiert...",
        }[loadingStep]
    }

    const download_excel = () => {
        get(
            "devices/" + props.device?.props.uuid + "/reports/yearV1/excel?year=" + date.format("YYYY"),
        ).fail(
            (e) => {
                message.error("Excel konnte nicht heruntergeladen werden!");
                console.log(e);
            }
        ).done(
            () => setExcelAvailable(false)
        )
    }

    const reportEditor = () => {
        switch (state) {
            case ViewState.initial:
            case ViewState.loading:
                return <Result icon={<LoadingOutlined spin/>} subTitle={loadingText()}
                ><Skeleton
                    active/></Result>

            case ViewState.success:
                return <Center>
                    <div style={{maxWidth: "21cm", minWidth: 'min(21cm, 100%)'}}>
                        <Space size={24} style={{width: "100%", justifyContent: "right"}}>

                        </Space>
                        <br/>
                        <br/>
                        {reportBody.map((part: any) => <>{part.item}<br/></>)}
                        <br/>
                        <Space size={24} style={{width: "100%", justifyContent: "right"}}>
                            <Tooltip
                                overlay={excelAvailable ? "Das erstellen der Excel kann bei vielen Untersuchungen mehrere Stunden benötigen." : "Excel nicht verfügbar / bereits angefordert"}
                            >
                                <Button onClick={download_excel} type="default" disabled={!excelAvailable}>Excel
                                    herunterladen</Button>
                            </Tooltip>
                            <Button onClick={preview} type="default">Vorschau</Button>
                            <Button onClick={onSubmit} type="primary">Bericht erzeugen</Button>
                        </Space>
                    </div>
                </Center>

            case ViewState.failed:
            default:
                return <Result
                    status="500"
                    title="Fehler"
                    subTitle="Entschuldigung, die Berichte konnten nicht geladen werden!"
                />
        }
    }

    const progressBar = <Steps current={step}
                               onChange={setStep}
                               items={[
                                   {
                                       title: "Zeitraum auswählen",
                                   },
                                   {
                                       title: "Bericht bearbeiten",
                                       status: state === ViewState.initial ? "wait" : state === ViewState.loading ? "process" : state === ViewState.success ? (step === 1 ? "process" : "finish") : "error"
                                   },
                                   {
                                       title: "Bericht generieren",
                                   }
                               ]}/>

    const bodys = [
        (dateSelector),
        (reportEditor()),
        (<Result status="success" title="Bericht wird erzeugt"
                 subTitle="Das PDF wird nach Abschluss automatisch herruntergeladen."/>),
    ]

    return <>
        {progressBar}
        <br/><br/>
        {bodys[step]}
    </>


}

export const MonthV1ReportEditor = (props: ReportEditorProps) => {
    // general variables
    const [state, setState] = useState(ViewState.initial);
    const [step, setStep] = useState(0);
    const [loadingStep, setLoadingStep] = useState(0);

    // --------------------------------------------------
    // Step 1: Date selection
    const [date, setDate] = useState(dayjs().subtract(1, 'months').startOf('month')
    );
    const fetchData = () => {
        if (date !== null) {
            setState(ViewState.loading)
            setStep(1);

            // @ts-ignore
            get("devices/" + props.device?.props.uuid + "/reports/month_v1/data?month=" + date.format("YYYYMM"))
                .done((response) => {
                        setState(ViewState.success)
                        setReportRawData(response.data.report_rawdata)
                    }
                )
                .fail(() => setState(ViewState.failed))
        } else {
            message.warning("Zeitraum benötigt!")
        }
    }

    const dateSelector = (<Space size={24} style={{width: "100%", justifyContent: "center"}}>
        Monat: <DatePicker.MonthPicker
        defaultValue={
            dayjs().subtract(1, 'months').startOf('month')
        }
        format={"MMM YYYY"}
        placement={'bottomRight'}
        // @ts-ignore
        onChange={(value) => setDate(value)}
    />
        <Button onClick={fetchData} children={"Daten abrufen"}/>
    </Space>)

    // --------------------------------------------------
    // Step 2: Data editor
    const [reportRawData, setReportRawData] = useState({
        calculated: null,
        manual: null,
        order: null,
        month: null,
    });
    const [manualEntry, setManualEntry] = useState({} as ManualEntry);
    const [rawDataTableBody, setRawDataTableBody] = useState(<tbody></tbody>);
    const [manualEntryInputRefs, setManualEntryInputRefs] = useState({});

    const rawDataTable = () => (
        <table
            style={{
                width: "100%",
                borderCollapse: "collapse",
            }}
        >
            <thead style={{
                textAlign: 'right',
            }}>
            <tr>
                <th></th>
                <th>Berechnete Werte</th>
                <th>Manuelle Eingabe</th>
                <th>Ausblenden</th>
            </tr>
            </thead>
            {rawDataTableBody}
        </table>
    )

    useEffect(() => {
        if (reportRawData?.order === null) {
            setManualEntry(new ManualEntry({
                device: {'uuid': props.device?.props.uuid}
            }));
            setRawDataTableBody(<tbody></tbody>);
            return;
        }

        let manual_entry: ManualEntry
        if (reportRawData?.manual === null) {
            manual_entry = new ManualEntry({
                device: {'uuid': props.device?.props.uuid}
            });
            manual_entry.props.month = reportRawData?.month ? dayjs(reportRawData.month, "YYYYMM") : undefined;
        } else {
            manual_entry = new ManualEntry(reportRawData?.manual);
        }

        setManualEntry(manual_entry);

        let refs = {}
        // @ts-ignore
        setRawDataTableBody(
            <tbody>
            {
                // @ts-ignore
                reportRawData?.order?.map(
                    (row: string) => {
                        let ref = createRef();
                        // @ts-ignore
                        refs[row[1]] = ref;
                        return (
                            <ManualEntryInput
                                // @ts-ignore
                                ref={ref}
                                key={row[1]}
                                name={row[0]}
                                calculated={reportRawData?.calculated?.[row[1]]}
                                value={reportRawData?.manual ? reportRawData.manual[row[1]] : null}
                            />
                        )
                    }
                )
            }
            </tbody>
        )
        setManualEntryInputRefs(refs);

    }, [reportRawData, props.device?.props.uuid])

    const loadingRaw = () => {
        setTimeout(() => setLoadingStep((loadingStep + 1) % 3), 3000);
        return {
            0: "Monatsdaten werden abgerufen...",
            1: "Tabelle wird erstellt ...",
            2: "Werte werden berechnet ...",
        }[loadingStep]
    }

    const uploadManualEntry = () => {
        setState(ViewState.loading)
        let data = {}
        console.debug("manualEntryInputsRef", manualEntryInputRefs);
        // @ts-ignore
        reportRawData?.order?.forEach((row: any) => {
            // @ts-ignore
            data[row[1]] = manualEntryInputRefs[row[1]].current.get();
        })
        console.debug("manualEntryInputData", data);

        manualEntry.update(data);

        console.debug("manualEntry", manualEntry);

        let req: JQueryPromise<any>;
        if (manualEntry?.props?.uuid === undefined) {
            req = post("/api/v1/manualentrys", manualEntry.dump())
        } else {
            req = patch("/api/v1/manualentrys/" + manualEntry.props.uuid, manualEntry.dump())
        }

        req.then(
            () => {
                setState(ViewState.loading)
                setStep(2);
                setTimeout(fetchText, 500);
            },
            (e) => {
                message.error("Speichern der manuellen Eingabe fehlgeschlagen!");
                console.error(e);
                setState(ViewState.success);
            }
        )
    }

    const fetchText = () => {
        // @ts-ignore
        get("devices/" + props.device?.props.uuid + "/reports/month_v1/text?month=" + date.format("YYYYMM"))
            .done((response) => {
                setState(ViewState.success)
                setReportTextData(response.data.report_textdata)
            })
            .fail(() => setState(ViewState.failed))
    }

    const dataEditor = () => {
        switch (state) {
            case ViewState.initial:
            case ViewState.loading:
                return <Center>
                    <div style={{maxWidth: "21cm", minWidth: 'min(21cm, 100%)'}}>
                        <Card
                            title=" "
                            type="inner"
                        >
                            <Result icon={<LoadingOutlined spin/>} subTitle={loadingRaw()}>
                                <Skeleton active/>
                            </Result>
                        </Card>
                        <br/>
                        <Space size={24} style={{width: "100%", justifyContent: "right"}}>
                            <Skeleton.Button active style={{width: '15em'}}/>
                        </Space>
                    </div>
                </Center>


            case ViewState.success:
                return <Center>
                    <div style={{maxWidth: "21cm", minWidth: 'min(21cm, 100%)'}}>
                        <Card
                            title={(manualEntry?.props?.month ? manualEntry.props.month.format("MMMM YYYY") : "")}
                            type="inner"
                        >
                            {rawDataTable()}
                        </Card>
                        <br/>
                        <Space size={24} style={{width: "100%", justifyContent: "right"}}>
                            <Button onClick={uploadManualEntry} type="primary">Speichern & Text bearbeiten</Button>
                        </Space>
                    </div>
                </Center>

            case ViewState.failed:
            default:
                return <Result
                    status="500"
                    title="Fehler"
                    subTitle="Entschuldigung, die Berichtsdaten konnten nicht geladen werden!"
                />
        }
    }


    // --------------------------------------------------
    // Step 3: Report editor
    const [reportTextData, setReportTextData] = useState({});
    const [reportBody, setReportBody] = useState([]);
    useEffect(() => {
        console.debug("reportTextData", reportTextData);
        // @ts-ignore
        if ('parts' in reportTextData && reportTextData['parts'] !== null) {
            let body: any[] = []
            // @ts-ignore
            for (let part of reportTextData['parts']) {
                switch (part['type']) {
                    case 'layout-text':
                        body = [...body, {
                            type: 'layout-text',
                            ref: part['content'],
                            item: <p
                                style={{textAlign: part['align'] ? part['align'] : 'left'}}>{breakLine(part['content'])}</p>
                        }]
                        break;
                    case 'text':
                        body = [...body, {
                            type: 'text',
                            ref: part['content'],
                            item: <p
                                style={{textAlign: part['align'] ? part['align'] : 'left'}}>{breakLine(part['content'])}</p>
                        }]
                        break;
                    case 'editor':
                        let editor = createRef();
                        body = [...body, {
                            type: 'editor',
                            ref: editor,
                            item: <RichTextEditor
                                ref={editor}
                                colors={DEFAULT_COLORS}
                                content={part['content']}
                                placeholder={"Hier Freitext eingeben..."}
                            />
                        }]
                        break;
                    case 'address':
                        let address = createRef();
                        body = [...body, {
                            type: 'address',
                            ref: address,
                            item: <RichTextEditor
                                disableFormating
                                ref={address}
                                colors={DEFAULT_COLORS}
                                content={part['content']}
                                placeholder={"Hier Freitext eingeben..."}
                            />
                        }]
                        break;
                    case 'subject':
                        let subject = createRef();
                        body = [...body, {
                            type: 'subject',
                            ref: subject,
                            item: <RichTextEditor
                                disableFormating
                                ref={subject}
                                colors={DEFAULT_COLORS}
                                content={part['content']}
                                placeholder={"Hier Freitext eingeben..."}
                            />
                        }]
                        break;
                    case 'image':
                        body = [...body, {
                            type: 'image',
                            ref: part['uuid'],
                            item: <Center><AsyncImage style={{maxHeight: 250, width: 'auto'}} uuid={part['uuid']}/><br/></Center>
                        }]
                        break;
                    case 'table':
                        let table = createRef();
                        body = [...body, {
                            type: 'table',
                            ref: table,
                            item: <Center><AsyncTable ref={table} uuid={part['uuid']}/></Center>

                        }]
                        break;
                    default:
                        break;
                }
                console.debug("part", part);
            }
            // @ts-ignore
            setReportBody(body);
        } else {
            setReportBody([]);
        }
    }, [reportTextData])

    const loadingText = () => {
        setTimeout(() => setLoadingStep((loadingStep + 1) % 3), 3000);
        return {
            0: "Bericht wird vorbereitet...",
            1: "Texte werden generiert...",
            2: "Layout wird formatiert...",
        }[loadingStep]
    }
    const generatePdf = () => {
        setStep(3);
        let report: any[] = []
        for (let part of reportBody) {
            // @ts-ignore
            if (part.type === 'editor') {
                // @ts-ignore
                report = [...report, {type: 'editor', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'address') {
                // @ts-ignore
                report = [...report, {type: 'address', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'subject') {
                // @ts-ignore
                report = [...report, {type: 'subject', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'image') {
                // @ts-ignore
                report = [...report, {type: 'image', uuid: part.ref}]
            }
            // @ts-ignore
            if (part.type === 'text') {
                // @ts-ignore
                report = [...report, {type: 'text', content: part.ref}]
            }
            // @ts-ignore
            if (part.type === 'layout-text') {
                // @ts-ignore
                report = [...report, {type: 'layout-text', content: part.ref}]
            }
            // @ts-ignore
            if (part.type === 'table') {
                // @ts-ignore
                report = [...report, {type: 'table', table: part.ref.current.content}]
            }
        }
        post(
            "devices/" + props.device?.props.uuid + "/reports/month_v1",
            {
                'report': report,
                'month': date.format("YYYYMM"),
            }
        )
    }
    const reportEditor = () => {
        switch (state) {
            case ViewState.initial:
            case ViewState.loading:
                return <Center>
                    <div style={{maxWidth: "21cm", minWidth: 'min(21cm, 100%)'}}>
                        <Card type="inner"><Skeleton title={false} active/></Card>
                        <Result icon={<LoadingOutlined spin/>} subTitle={loadingText()}/>
                        <Card type="inner"><Skeleton title={false} active/></Card>
                        <br/>
                        <Card title=" " type="inner"><Skeleton active/></Card>
                        <br/>
                        <Space size={24} style={{width: "100%", justifyContent: "right"}}>
                            <Skeleton.Button active style={{width: '15em'}}/>
                        </Space>
                    </div>
                </Center>

            case ViewState.success:
                return <Center>
                    <div style={{maxWidth: "21cm", minWidth: 'min(21cm, 100%)'}}>
                        {reportBody.map((part: any) => <>{part.item}<br/></>)}
                        <br/>
                        <Space size={24} style={{width: "100%", justifyContent: "right"}}>
                            <Button onClick={generatePdf} type="primary">Bericht erzeugen</Button>
                        </Space>
                    </div>
                </Center>

            case ViewState.failed:
            default:
                return <Result
                    status="500"
                    title="Fehler"
                    subTitle="Entschuldigung, der Bericht konnten nicht geladen werden!"
                />
        }
    }

    // --------------------------------------------------
    // General Page Layout
    const get_status = (i: number) => {
        switch (state) {
            case ViewState.success:
                if (step < i) {
                    return "wait"
                } else if (step === i) {
                    return "process"
                } else {
                    return "finish"
                }
            case ViewState.failed:
                if (step === i) {
                    return "error"
                } else if (step > i) {
                    return "finish"
                } else {
                    return "wait"
                }
            case ViewState.initial:
            case ViewState.loading:
            default:
                return "wait"
        }
    }

    const progressBar = <Steps current={step}
                               onChange={setStep}
                               items={[
                                   {
                                       title: "Zeitraum auswählen",
                                   },
                                   {
                                       title: "Daten prüfen",
                                       status: get_status(1)
                                   },
                                   {
                                       title: "Text editieren",
                                       status: get_status(2)
                                   },
                                   {
                                       title: "Bericht generieren",
                                       status: get_status(3)
                                   }
                               ]}/>

    const bodys = [
        (dateSelector),
        (dataEditor()),
        (reportEditor()),
        (<Result status="success" title="Bericht wird erzeugt"
                 subTitle="Das PDF wird nach Abschluss automatisch herruntergeladen."/>),
    ]

    return <>
        {progressBar}
        <br/><br/>
        {bodys[step]}
    </>


}

// DateSelector
interface DateSelectorProps {
    setStep: (step: number) => void,
    setParentState: (state: ViewState) => void,
    setDate: (date: dayjs.Dayjs) => void,
    date: dayjs.Dayjs,
    mode: "month" | "year",
}

const DateSelector = (props: DateSelectorProps) => {

    const fetchData = () => {
        if (props.date !== null) {
            props.setStep(1);
            props.setParentState(ViewState.loading);
        } else {
            message.warning("Zeitraum benötigt!")
        }
    }

    switch (props.mode) {
        case "year":
            return (
                <Space size={24} style={{width: "100%", justifyContent: "center"}}>
                    Jahr: <DatePicker.YearPicker
                    defaultValue={
                        dayjs().subtract(1, 'years').endOf('year')
                    }
                    format={"YYYY"}
                    placement={'bottomRight'}
                    // @ts-ignore
                    onChange={(value) => props.setDate(value)}
                />
                    <Button onClick={fetchData} children={"Daten abrufen"}/>
                </Space>
            )

        default:
        case "month":
            return (
                <Space size={24} style={{width: "100%", justifyContent: "center"}}>
                    Monat: <DatePicker.MonthPicker
                    defaultValue={
                        dayjs().subtract(1, 'months').startOf('month')
                    }
                    format={"MMM YYYY"}
                    placement={'bottomRight'}
                    // @ts-ignore
                    onChange={(value) => props.setDate(value)}
                />
                    <Button onClick={fetchData} children={"Daten abrufen"}/>
                </Space>
            )
    }
}

// PreCalcChecker
interface PreCalcCheckerProps {
    device: Device | null,
    date: dayjs.Dayjs,
    mode: "month" | "year",
    setStep: (step: number) => void,
    setParentState: (state: ViewState) => void,
}

const PreCalcChecker = (props: PreCalcCheckerProps) => {
    const [state, setState] = useState(ViewState.loading);
    const [created_at, setCreatedAt] = useState<dayjs.Dayjs | null>(null);
    const [loadingStep, setLoadingStep] = useState(0);
    const [checkingStep, setCheckingStep] = useState(1);
    const [doRecalculate, setDoRecalculate] = useState(false);
    //0=failed,
    // 1=checking unfiltered, 2= waiting for unfiltered, 3 = getting done job,
    // 4=checking   filtered, 5= waiting for   filtered, 6 = getting done job,
    // 7=done
    const [jobUuid, setJobUuid] = useState<string | null>(null);

    useEffect(() => {
        checkPreCalc(false);
    }, []);

    const checkPreCalc = (recalculate: boolean) => {
        setState(ViewState.loading);
        props.setParentState(ViewState.loading);
        setDoRecalculate(recalculate);
        setCheckingStep(1);
    }

    useEffect(() => checkSteps(), [checkingStep]);// execute step on checkingStep change

    const checkSteps = () => {
        switch (checkingStep) {
            case 0:
                break
            case 1:
            case 3:
            case 4:
            case 6:
                // checking for precalc
                let recalc = doRecalculate ? ((checkingStep === 1 || checkingStep === 4) ? "true" : "false") : "false";
                let filtered = (checkingStep === 4 || checkingStep === 6) ? "true" : "false";
                get("devices/" + props.device?.props.uuid + "/reports/check_precalc"
                    + "?recalculate=" + recalc
                    + "&year=" + props.date.format("YYYY")
                    + "&month=" + (props.mode === "year" ? "0" : props.date.format("MM"))
                    + "&filtered=" + filtered
                ).done((response) => {
                    if (response?.data?.exists === true) {  // precalc exists
                        // set created_at if earlier
                        const date = response?.data?.created_at ? dayjs(response.data.created_at) : null;
                        if (created_at === null || (date !== null && date.isBefore(created_at))) {
                            setCreatedAt(date);
                        }
                        if (checkingStep === 1 || checkingStep === 4) {
                            setCheckingStep(checkingStep + 3); // skip waiting for job
                        } else {
                            setCheckingStep(checkingStep + 1); // we just waited, next step
                        }

                    } else {  // precalc does not exist, job started
                        setJobUuid(response?.data?.job_uuid ? response.data.job_uuid : null)  // set job uuid
                        setCheckingStep(checkingStep + 1);  // go to waiting for job
                    }
                }).fail(() => {
                    setCheckingStep(0);
                    setState(ViewState.failed);
                    props.setParentState(ViewState.failed);
                })
                break
            case 2:
            case 5:
                if (jobUuid === null) {
                    // should not happen
                    setCheckingStep(0);
                    setState(ViewState.failed);
                    props.setParentState(ViewState.failed);
                    break;
                }
                // check if job is done
                get("download/" + jobUuid + "/status")
                    .done(
                        (data, status, jqXHR) => {
                            switch (jqXHR.status) {
                                case 202:
                                    setTimeout(checkSteps, 5000);  // check again in 5 seconds
                                    break;
                                case 200:
                                case 204:
                                case 206:
                                    setJobUuid(null); // clear job uuid
                                    setCheckingStep(checkingStep + 1) // next step fetching just calculated precalc
                                    break;
                                default:
                                    // unknown status, should not happen
                                    setCheckingStep(0);
                                    setState(ViewState.failed);
                                    props.setParentState(ViewState.failed);
                                    break;
                            }
                        }
                    ).fail(() => {
                    setCheckingStep(0);
                    setState(ViewState.failed);
                    props.setParentState(ViewState.failed);
                })
                break
            case 7:
                // done
                setState(ViewState.success);
                props.setParentState(ViewState.success);
                break
            default:
                break;
        }
    }

    const loadingRaw = () => {
        return {
            0: "",
            1: "Ungefilterte Untersuchungen werden geprüft ...",
            2: "Ungefilterte Untersuchungen werden vorbereitet ...",
            3: "Ungefilterte Untersuchungen werden abgerufen ...",
            4: "Gefilterte Untersuchungen werden geprüft ...",
            5: "Gefilterte Untersuchungen werden vorbereitet ...",
            6: "Gefilterte Untersuchungen werden abgerufen ...",
            7: "Fertig",

        }[checkingStep]
    }

    switch (state) {
        case ViewState.initial:
        case ViewState.loading:
            return <Center>
                <div style={{maxWidth: "21cm", minWidth: 'min(21cm, 100%)'}}>
                    <Card
                        title={props.mode === "month" ?
                            props.date.format("MMMM YYYY") :
                            props.date.format("YYYY")}
                        type="inner"
                    >
                        <Result
                            icon={<Progress type='circle' percent={checkingStep * 14}/>}
                            subTitle={loadingRaw()}/>
                    </Card>
                    <br/>
                    <Space size={24} style={{width: "100%", justifyContent: "right"}}>
                        <Skeleton.Button active style={{width: '15em'}}/>
                    </Space>
                </div>
            </Center>
        case ViewState.success:
            return <Center>
                <div style={{maxWidth: "21cm", minWidth: 'min(21cm, 100%)'}}>
                    <Card
                        title={props.mode === "month" ?
                            props.date.format("MMMM YYYY") :
                            props.date.format("YYYY")}
                        type="inner"
                    >
                        <Result
                            status="success"
                            title="Daten vorbereitet"
                            subTitle={
                                "Die Berechnung wurde " +
                                (created_at !== null ?
                                    //ts-ignore
                                    "am " + created_at.toString() :
                                    "vor kurzem")
                                +
                                " durchgeführt."
                            }
                            extra={
                                <Button onClick={() => checkPreCalc(true)}>Neu berechnen</Button>
                            }
                        />
                    </Card>
                    <br/>
                    <Space size={24} style={{width: "100%", justifyContent: "right"}}>
                        <Button onClick={() => {
                            props.setStep(2);
                            props.setParentState(ViewState.loading);
                        }} type="primary">Weiter</Button>
                    </Space>
                </div>
            </Center>
        case ViewState.failed:
        default:
            return <Result
                status="500"
                title="Fehler"
                subTitle="Entschuldigung, die Berichtsdaten konnten nicht geladen werden!"
            />
    }

}

// DataEditor
interface ManualEntryInputProps {
    key: string,
    name: string,
    calculated: number | null | undefined,
    value: number | null,
}

const ManualEntryInput = forwardRef((props: ManualEntryInputProps, ref) => {
    const [value, setValue] = useState(props.value);

    useImperativeHandle(ref, () => ({
        get() {
            return value;
        }
    }));

    return (
        <tr>
            <td
                align='right'
                style={{textDecoration: value === -1 ? 'line-through' : 'none'}}
            >{props.name}</td>
            <td
                align='right'
                style={{textDecoration: value === -1 ? 'line-through' : 'none'}}
            >
                {
                    props.calculated !== null ? props.calculated : ""
                }
            </td>
            <td align='right'>
                <InputNumber
                    style={{width: '10em'}}
                    decimalSeparator={","}
                    controls={false}
                    disabled={value === -1}
                    value={value === -1 ? "" : value}
                    // @ts-ignore
                    onChange={(value) => setValue(value)}
                />
            </td>
            <td align='right'>
                <Tooltip
                    mouseEnterDelay={.5}
                    overlay={"Diesen Wert im Bericht ignorieren"}
                    placement={"left"}
                >
                    <Checkbox
                        checked={
                            value === -1
                        }
                        onChange={
                            (e) => {
                                setValue(e.target.checked ? -1 : null);
                            }
                        }
                    />
                </Tooltip>
            </td>
        </tr>
    )
});

interface DataEditorProps {
    device: Device | null,
    setStep: (step: number) => void,
    setParentState: (state: ViewState) => void,
    date: dayjs.Dayjs,
    mode: "month" | "year",
}

const DataEditor = (props: DataEditorProps) => {
    const [state, setState] = useState(ViewState.loading);
    const [manualEntry, setManualEntry] = useState({} as ManualEntry);
    const [rawDataTableBody, setRawDataTableBody] = useState(<tbody></tbody>);
    const [manualEntryInputRefs, setManualEntryInputRefs] = useState({});
    const [loadingStep, setLoadingStep] = useState(0);
    const [reportRawData, setReportRawData] = useState({
        calculated: null,
        manual: null,
        order: null,
        month: null,
        year: null,
        excel_available: false,
    });
    const [excelAvailable, setExcelAvailable] = useState(false);


    const {
        setStep,
        setParentState,
        date,
    } = props;

    useEffect(() => {
        setParentState(ViewState.loading);
        // @ts-ignore
        get("devices/" + props.device?.props.uuid + "/reports/" + props.mode + "/values?" + props.mode + "=" + date.format(props.mode === "month" ? "YYYYMM" : "YYYY")
        )
            .done((response) => {
                    setState(ViewState.success)
                    setParentState(ViewState.success)
                    setReportRawData(response.data.report_values)
                }
            )
            .fail(() => {
                setState(ViewState.failed);
                setParentState(ViewState.failed);
            })
    }, [props.device?.props.uuid, date])


    const rawDataTable = () => (
        <table
            style={{
                width: "100%",
                borderCollapse: "collapse",
            }}
        >
            <thead style={{
                textAlign: 'right',
            }}>
            <tr>
                <th></th>
                <th>Berechnete Werte</th>
                <th>Manuelle Eingabe</th>
                <th>Ausblenden</th>
            </tr>
            </thead>
            {rawDataTableBody}
        </table>
    )

    useEffect(() => {
        if (reportRawData?.excel_available !== undefined) {
            setExcelAvailable(reportRawData?.excel_available as boolean)
        }

        if (reportRawData?.order === null) {
            setManualEntry(new ManualEntry({
                device: {'uuid': props.device?.props.uuid}
            }));
            setRawDataTableBody(<tbody></tbody>);
            return;
        }

        let manual_entry: ManualEntry
        if (reportRawData?.manual === null) {
            manual_entry = new ManualEntry({
                device: {'uuid': props.device?.props.uuid}
            });
            if (props.mode === "month") {
                manual_entry.props.month = reportRawData?.month ? dayjs(reportRawData.month, "YYYYMM") : undefined;
            } else {
                manual_entry.props.year = reportRawData?.year ? dayjs(reportRawData.year, "YYYY") : undefined;
            }
        } else {
            manual_entry = new ManualEntry(reportRawData?.manual);
        }

        setManualEntry(manual_entry);

        let refs = {}
        // @ts-ignore
        setRawDataTableBody(
            <tbody>
            {
                // @ts-ignore
                reportRawData?.order?.map(
                    (row: string) => {
                        let ref = createRef();
                        // @ts-ignore
                        refs[row[1]] = ref;
                        return (
                            <ManualEntryInput
                                // @ts-ignore
                                ref={ref}
                                key={row[1]}
                                name={row[0]}
                                calculated={reportRawData?.calculated?.[row[1]]}
                                value={reportRawData?.manual ? reportRawData.manual[row[1]] : null}
                            />
                        )
                    }
                )
            }
            </tbody>
        )
        setManualEntryInputRefs(refs);

    }, [reportRawData])

    const loadingRaw = () => {
        setTimeout(() => setLoadingStep((loadingStep + 1) % 3), 3000);
        return {
            0: "Monatsdaten werden abgerufen...",
            1: "Texte werden erstellt ...",
            2: "Bilder werden vorbereitet ...",
        }[loadingStep]
    }

    const uploadManualEntry = () => {
        setState(ViewState.loading)
        setParentState(ViewState.loading);
        let data = {}
        console.debug("manualEntryInputsRef", manualEntryInputRefs);
        // @ts-ignore
        reportRawData?.order?.forEach((row: any) => {
            // @ts-ignore
            data[row[1]] = manualEntryInputRefs[row[1]].current.get();
        })
        console.debug("manualEntryInputData", data);

        manualEntry.update(data);

        console.debug("manualEntry", manualEntry);

        let req: JQueryPromise<any>;
        if (manualEntry?.props?.uuid === undefined) {
            req = post("/api/v1/manualentrys", manualEntry.dump())
        } else {
            req = patch("/api/v1/manualentrys/" + manualEntry.props.uuid, manualEntry.dump())
        }

        req.then(
            () => {
                next()
            },
            (e) => {
                message.error("Speichern der manuellen Eingabe fehlgeschlagen!");
                console.error(e);
                setState(ViewState.success);
                setParentState(ViewState.success);
            }
        )
    }

    const download_excel = () => {
        get(
            "devices/" + props.device?.props.uuid + "/reports/" + props.mode + "/excel?" + props.mode + "=" + date.format(props.mode === "month" ? "YYYYMM" : "YYYY")
        ).fail(
            (e) => {
                message.error("Excel konnte nicht heruntergeladen werden!");
                console.log(e);
            }
        ).done(
            () => setExcelAvailable(false)
        )
    }


    const next = () => {
        setState(ViewState.loading)
        setParentState(ViewState.loading);
        setStep(3);
    }

    const dataEditor = () => {
        switch (state) {
            case ViewState.initial:
            case ViewState.loading:
                return <Center>
                    <div style={{maxWidth: "21cm", minWidth: 'min(21cm, 100%)'}}>
                        <Card
                            title=" "
                            type="inner"
                        >
                            <Result icon={<LoadingOutlined spin/>} subTitle={loadingRaw()}>
                                <Skeleton active/>
                            </Result>
                        </Card>
                        <br/>
                        <Space size={24} style={{width: "100%", justifyContent: "right"}}>
                            <Skeleton.Button active style={{width: '15em'}}/>
                        </Space>
                    </div>
                </Center>

            case ViewState.success:
                return <Center>
                    <div style={{maxWidth: "21cm", minWidth: 'min(21cm, 100%)'}}>
                        <Card
                            title={
                                props.mode === "month" ?
                                    (manualEntry?.props?.month ? manualEntry.props.month.format("MMMM YYYY") : "") :
                                    (manualEntry?.props?.year ? manualEntry.props.year.format("YYYY") : "")}
                            type="inner"
                        >
                            {rawDataTable()}
                        </Card>
                        <br/>
                        <Tooltip
                            overlay={excelAvailable ? "Das erstellen der Excel kann bei vielen Untersuchungen mehrere Stunden benötigen." : "Excel nicht verfügbar / bereits angefordert"}
                        >
                            <Button onClick={download_excel} type="default" disabled={!excelAvailable}>Excel
                                herunterladen</Button>
                        </Tooltip>
                        <Space size={24} style={{float: "right"}}>
                            <Button onClick={next}>ohne Speichern weiter</Button>
                            <Button onClick={uploadManualEntry} type="primary">Speichern & Weiter</Button>
                        </Space>
                    </div>
                </Center>
            case ViewState.failed:
            default:
                return <Result
                    status="500"
                    title="Fehler"
                    subTitle="Entschuldigung, die Berichtsdaten konnten nicht geladen werden!"
                />
        }
    }

    return dataEditor();
}

// TextEditor
interface TextEditorProps {
    device: Device | null,
    setStep: (step: number) => void,
    setParentState: (state: ViewState) => void,
    date: dayjs.Dayjs,
    mode: "month" | "year",
}

const TextEditor = (props: TextEditorProps) => {
    const [state, setState] = useState(ViewState.loading);
    const [reportTextData, setReportTextData] = useState({});
    const [reportBody, setReportBody] = useState<any[]>([]);
    const [loadingStep, setLoadingStep] = useState(0);
    const {token} = useToken();

    const {
        setStep,
        setParentState,
        date,
    } = props;

    useEffect(() => {
        setParentState(ViewState.loading);
        setTimeout(fetchText, 500);
    }, [])

    const fetchText = () => {
        // @ts-ignore
        get("devices/" + props.device?.props.uuid + "/reports/" + props.mode + "/text?" + props.mode + "=" + date.format(props.mode === "month" ? "YYYYMM" : "YYYY"))
            .done((response) => {
                setState(ViewState.success)
                setParentState(ViewState.success)
                setReportTextData(response.data.report_text)
            })
            .fail(() => {
                setState(ViewState.failed);
                setParentState(ViewState.failed);
            })
    }

    useEffect(() => {
        console.debug("reportTextData", reportTextData);
        // @ts-ignore
        if ('parts' in reportTextData && reportTextData['parts'] !== null) {
            let body: any[] = []
            // @ts-ignore
            reportTextData["parts"].forEach((part, index) => {
                switch (part['type']) {
                    case 'layout-text':
                        body = [...body, {
                            id: index,
                            type: 'layout-text',
                            ref: part['content'],
                            item: <p
                                style={{textAlign: part['align'] ? part['align'] : 'left'}}>{breakLine(part['content'])}</p>,
                            visible: true,
                            shortDescription: "Zeitraum, Gerätetyp, Alias, Standort",

                        }]
                        break;
                    case 'text':
                        body = [...body, {
                            id: index,
                            type: 'text',
                            ref: part['content'],
                            item: <p
                                style={{textAlign: part['align'] ? part['align'] : 'left'}}>{breakLine(part['content'])}</p>,
                            visible: true,
                            shortDescription: "Text"
                        }]
                        break;
                    case 'editor':
                        let editor = createRef();
                        body = [...body, {
                            id: index,
                            type: 'editor',
                            ref: editor,
                            item: <RichTextEditor
                                ref={editor}
                                colors={DEFAULT_COLORS}
                                content={part['content']}
                                placeholder={"Hier Freitext eingeben..."}
                            />,
                            visible: true,
                            shortDescription: "Editor"
                        }]
                        break;
                    case 'address':
                        let address = createRef();
                        body = [...body, {
                            id: index,
                            type: 'address',
                            ref: address,
                            item: <RichTextEditor
                                disableFormating
                                ref={address}
                                colors={DEFAULT_COLORS}
                                content={part['content']}
                                placeholder={"Hier Freitext eingeben..."}
                            />,
                            visible: true,
                            shortDescription: "Adresse"
                        }]
                        break;
                    case 'subject':
                        let subject = createRef();
                        body = [...body, {
                            id: index,
                            type: 'subject',
                            ref: subject,
                            item: <RichTextEditor
                                disableFormating
                                ref={subject}
                                colors={DEFAULT_COLORS}
                                content={part['content']}
                                placeholder={"Hier Freitext eingeben..."}
                            />,
                            visible: true,
                            shortDescription: "Betreff"
                        }]
                        break;
                    case 'image':
                        let width = part['width'] ? part['width'] : 'auto';
                        let style = {}
                        if (width === 'auto') {
                            style = {maxHeight: 250, width: 'auto'}
                        } else {
                            style = {'height': 'auto', maxWidth: width * 21 + 'cm'}
                        }
                        body = [...body, {
                            id: index,
                            type: 'image',
                            ref: {'uuid': part['uuid'], 'width': part['width'] ? part['width'] : '0.65'},
                            item: <Center>
                                <AsyncImage style={style} uuid={part['uuid']}/><br/>
                            </Center>,
                            visible: true,
                            shortDescription: "Bild"
                        }]
                        break;
                    case 'table':
                        let table = createRef();
                        body = [...body, {
                            id: index,
                            type: 'table',
                            ref: table,
                            item: <Center><AsyncTable ref={table} uuid={part['uuid']}/></Center>,
                            visible: true,
                            shortDescription: "Tabelle"

                        }]
                        break;
                    default:
                        break;
                }
            })
            // @ts-ignore
            setReportBody(body);
        } else {
            setReportBody([]);
        }
    }, [reportTextData])

    const loadingText = () => {
        setTimeout(() => setLoadingStep((loadingStep + 1) % 3), 3000);
        return {
            0: "Bericht wird vorbereitet...",
            1: "Texte werden generiert...",
            2: "Layout wird formatiert...",
        }[loadingStep]
    }

    const build_report_from_parts = (reportBody: any) => {

        let report: any[] = []
        for (let part of reportBody) {
            // @ts-ignore
            if (part.type === 'editor' && part.visible) {
                // @ts-ignore
                report = [...report, {type: 'editor', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'address' && part.visible) {
                // @ts-ignore
                report = [...report, {type: 'address', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'subject' && part.visible) {
                // @ts-ignore
                report = [...report, {type: 'subject', content: part.ref.current.value}]
            } // @ts-ignore
            if (part.type === 'image' && part.visible) {
                // @ts-ignore
                report = [...report, {type: 'image', uuid: part.ref.uuid, width: part.ref.width}]
            }
            // @ts-ignore
            if (part.type === 'text' && part.visible) {
                // @ts-ignore
                report = [...report, {type: 'text', content: part.ref}]
            }
            // @ts-ignore
            if (part.type === 'layout-text' && part.visible) {
                // @ts-ignore
                report = [...report, {type: 'layout-text', content: part.ref}]
            }
            // @ts-ignore
            if (part.type === 'table' && part.visible) {
                // @ts-ignore
                report = [...report, {type: 'table', table: part.ref.current.content}]
            }
        }

        return report;
    }


    const generatePdf = () => {
        setStep(4);
        let report = build_report_from_parts(reportBody);
        post(
            "devices/" + props.device?.props.uuid + "/reports/" + props.mode + "/text",
            {
                'report': report,
                'month': date.format("YYYYMM"),  // upload both, the backend will select the right one
                'year': date.format("YYYY"),
            }
        )
    }


    const preview = () => {
        let report = build_report_from_parts(reportBody);
        post(
            "devices/" + props.device?.props.uuid + "/reports/" + props.mode + "/text?preview=true",
            {
                'report': report,
                'month': date.format("YYYYMM"),  // upload both, the backend will select the right one
                'year': date.format("YYYY"),
            }
        ).done(
            (response) => {
                // show pdf preview in modal
                Modal.info({
                    maskClosable: true,
                    closable: true,
                    okText: 'Schließen',
                    content: <PdfPreview uuid={response.data.job_uuid}/>,
                    width: 'calc(21cm + 48px)',
                    footer: null,
                    icon: null,
                    bodyStyle: {
                        width: '21cm',
                        paddingTop: 34,
                        height: 'calc(80vh-34px)',
                    },
                    style: {
                        minHeight: '80vh',
                        maxWidth: 'calc(21cm + 48px)',
                    },
                    className: 'pdf-preview',
                });
            }
        )
    }

    const changeVisibility = (part: any) => {
        const newPart = {...part, visible: !part.visible};
        const newReportBody = reportBody.map(p => p.id === part.id ? newPart : p);
        setReportBody(newReportBody);

    }


    const reportEditor = () => {
        switch (state) {
            case ViewState.initial:
            case ViewState.loading:
                return <Center>
                    <div style={{maxWidth: "21cm", minWidth: 'min(21cm, 100%)'}}>
                        <Card type="inner"><Skeleton title={false} active/></Card>
                        <Result icon={<LoadingOutlined spin/>} subTitle={loadingText()}/>
                        <Card type="inner"><Skeleton title={false} active/></Card>
                        <br/>
                        <Card title=" " type="inner"><Skeleton active/></Card>
                        <br/>
                        <Space size={24} style={{width: "100%", justifyContent: "right"}}>
                            <Skeleton.Button active style={{width: '15em'}}/>
                        </Space>
                    </div>
                </Center>

            case ViewState.success:
                return <Center>
                    <div style={{
                        maxWidth: "21cm",
                        minWidth: 'min(21cm, 100%)',
                    }}>
                        {reportBody.map((part: any) => {
                                let visibilityButton = (
                                    <CustomButton onClick={() => changeVisibility(part)}
                                                  mode={part.visible ? "visible" : "invisible"}
                                                  key={part.id}/>
                                )
                                let content = null;
                                switch (part.type) {
                                    case "layout-text" :    // no visibility changes
                                        content = part.item;
                                        break;
                                    case "text":
                                        content = (
                                            <Card title={
                                                <ContentBar children={visibilityButton}/>
                                            }>
                                                {part.item}
                                            </Card>
                                        );
                                        break;
                                    case "editor":
                                        content = React.cloneElement(part.item, {visibilityButton: visibilityButton});
                                        break;
                                    case "address":     // no visibility changes
                                        content = part.item;
                                        break;
                                    case "subject":     // no visibility changes
                                        content = part.item;
                                        break;
                                    case "image":
                                        content = (
                                            <Card headStyle={{backgroundColor: "#fafafa"}}
                                                  extra={visibilityButton}>
                                                {part.item}
                                            </Card>
                                        );
                                        break;
                                    case "table":
                                        content = React.cloneElement(part.item.props.children, {visibilityButton: visibilityButton});
                                        break;
                                }
                                return (
                                    <>
                                        <div style={
                                            {
                                                alignItems: "center",
                                                position: "relative",
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }
                                        }>
                                            <div style={
                                                {
                                                    display: "inline-block",
                                                    //maxWidth: "30cm",
                                                    minWidth: 'min(21cm, 100%)',
                                                    backgroundColor: "white",
                                                    color: 'black',
                                                    position: 'relative',
                                                    marginBottom: "5px",
                                                }
                                            }>
                                                {part.visible ?
                                                    content
                                                    :
                                                    (
                                                        <Card title={"Ausgeblendeter Inhalt: " + part.shortDescription}
                                                              extra={visibilityButton}
                                                              headStyle={
                                                                  {
                                                                      backgroundColor: "#fafafa",
                                                                      color: "#a5a5a5",
                                                                      fontWeight: "normal",
                                                                  }
                                                              }
                                                              bodyStyle={{
                                                                  display: 'none'
                                                              }}/>
                                                    )}
                                            </div>
                                        </div>
                                        <br/>
                                    </>
                                )
                            }
                        )
                        }
                        <br/>

                        <Space size={24} style={{width: "100%", justifyContent: "right"}}>
                            <Button onClick={preview} type="default">Vorschau</Button>
                            <Button onClick={generatePdf} type="primary">Bericht erzeugen</Button>
                        </Space>
                    </div>
                </Center>

            case ViewState.failed:
            default:
                return <Result
                    status="500"
                    title="Fehler"
                    subTitle="Entschuldigung, der Bericht konnten nicht geladen werden!"
                />
        }
    }

    return reportEditor();
}

// MonthReportEditor
export const MonthReportEditor = (props: ReportEditorProps) => {
    const [state, setState] = useState(ViewState.initial);
    const [step, setStep] = useState(0);
    const [body, setBody] = useState(<></>);
    const [date, setDate] = useState(dayjs().subtract(1, 'months').startOf('month'));

    useEffect(() => {
        if (step === 0) {
            setBody(<DateSelector
                setStep={setStep}
                setParentState={setState}
                setDate={setDate}
                date={date}
                mode={"month"}
            />);
        }
        if (step === 1) {
            setBody(<PreCalcChecker
                date={date}
                setParentState={setState}
                device={props.device}
                setStep={setStep}
                mode={"month"}
            />);
        }
        if (step === 2) {
            setBody(<DataEditor
                date={date}
                setParentState={setState}
                device={props.device}
                setStep={setStep}
                mode={"month"}
            />);
        }
        if (step === 3) {
            setBody(<TextEditor
                date={date}
                setParentState={setState}
                device={props.device}
                setStep={setStep}
                mode={"month"}
            />);
        }
        if (step === 4) {
            setBody(<Result status="success" title="Bericht wird erzeugt"
                            subTitle="Das PDF wird nach Abschluss automatisch herruntergeladen."/>);
        }
        if (step >= 5) {
            setStep(0);
        }
    }, [step])

    const get_status = (i: number) => {
        switch (state) {
            case ViewState.success:
                if (step < i) {
                    return "wait"
                } else if (step === i) {
                    return "process"
                } else {
                    return "finish"
                }
            case ViewState.failed:
                if (step === i) {
                    return "error"
                } else if (step > i) {
                    return "finish"
                } else {
                    return "wait"
                }
            case ViewState.initial:
            case ViewState.loading:
            default:
                if (step < i) {
                    return "wait"
                } else if (step === i) {
                    return "wait"
                } else {
                    return "finish"
                }
        }
    }

    return <>
        <Steps current={step}
               onChange={(step) => {
                   if (step === 0 || step === 1) {
                       setStep(step)
                   }
               }}
               items={[
                   {
                       title: "Zeitraum auswählen",
                   },
                   {
                       title: "Daten vorbereiten",
                       status: get_status(1),
                       disabled: step < 2,
                   },
                   {
                       title: "Daten prüfen",
                       status: get_status(2),
                       disabled: true,
                   },
                   {
                       title: "Text editieren",
                       status: get_status(3),
                       disabled: true,
                   },
                   {
                       title: "Bericht generieren",
                       status: get_status(4),
                       disabled: true,
                   }
               ]}
        />
        <br/>
        <br/>
        {body}
    </>
}

// YearReportEditor
export const YearReportEditor = (props: ReportEditorProps) => {
    const [state, setState] = useState(ViewState.initial);
    const [step, setStep] = useState(0);
    const [body, setBody] = useState(<></>);
    const [date, setDate] = useState(dayjs().subtract(1, 'years').endOf('year'));

    useEffect(() => {
        if (step === 0) {
            setBody(<DateSelector
                setStep={setStep}
                setParentState={setState}
                setDate={setDate}
                date={date}
                mode={"year"}
            />);
        }
        if (step === 1) {
            setBody(<PreCalcChecker
                date={date}
                setParentState={setState}
                device={props.device}
                setStep={setStep}
                mode={"year"}
            />);
        }
        if (step === 2) {
            setBody(<DataEditor
                date={date}
                setParentState={setState}
                device={props.device}
                setStep={setStep}
                mode={"year"}
            />);
        }
        if (step === 3) {
            setBody(<TextEditor
                date={date}
                setParentState={setState}
                device={props.device}
                setStep={setStep}
                mode={"year"}
            />);
        }
        if (step === 4) {
            setBody(<Result status="success" title="Bericht wird erzeugt"
                            subTitle="Das PDF wird nach Abschluss automatisch herruntergeladen."/>);
        }
        if (step >= 5) {
            setStep(0);
        }
    }, [step])

    const get_status = (i: number) => {
        switch (state) {
            case ViewState.success:
                if (step < i) {
                    return "wait"
                } else if (step === i) {
                    return "process"
                } else {
                    return "finish"
                }
            case ViewState.failed:
                if (step === i) {
                    return "error"
                } else if (step > i) {
                    return "finish"
                } else {
                    return "wait"
                }
            case ViewState.initial:
            case ViewState.loading:
            default:
                if (step < i) {
                    return "wait"
                } else if (step === i) {
                    return "wait"
                } else {
                    return "finish"
                }
        }
    }

    return <>
        <Steps current={step}
               onChange={(step) => {
                   if (step === 0 || step === 1) {
                       setStep(step)
                   }
               }}
               items={[
                   {
                       title: "Zeitraum auswählen",
                   },
                   {
                       title: "Daten vorbereiten",
                       status: get_status(1),
                       disabled: step < 2,
                   },
                   {
                       title: "Daten prüfen",
                       status: get_status(2),
                       disabled: true,
                   },
                   {
                       title: "Text editieren",
                       status: get_status(3),
                       disabled: true,
                   },
                   {
                       title: "Bericht generieren",
                       status: get_status(4),
                       disabled: true,
                   }
               ]}
        />
        <br/>
        <br/>
        {body}
    </>
}
